import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSettings } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import ComplianceScanModal from './ComplianceScanModal';
import ExportOptionsModal from './ExportOptionsModal';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import {
  ComplianceDashboardShell,
  CompliancePageHeader,
  ComplianceHeroRow,
  ComplianceFilterBar,
  ComplianceIssuesTable,
  ComplianceDetailPanel,
  ComplianceScanProgress,
} from './compliance-ui';
import {
  enrichIssueFields,
  applyRequirementRegisterContext,
  coerceSectionRef,
  normalizeIssueStatus,
  complianceTypeLabel,
  computeComplianceOverview,
  computeIssueKpis,
  issueMatchesComplianceFilters,
  formatLastScanAt,
  buildReviewNavStateFromIssue,
  issueHasDrawingReviewTarget,
} from './compliance-ui/complianceIssuePresenters';
import { DRAWING_REVIEW_ENABLED } from '../utils/featureFlags.js';
import { deriveComplianceScanStepIndex } from './compliance-ui/complianceScanProgress';
import { runComplianceExport } from './compliance-ui/complianceExport';
import { buildAuthHeaders } from '../utils/openPdfReference';
import { createPdfReferenceHandlers } from '../utils/openPdfReference';
import PdfViewerModal from './PdfViewerModal';
import ProjectService from '../services/projectService.js';
import {
  phScanPathSelected,
  phScanRun,
  phScanFailed,
  phScanCompleted,
  phFindingGenerated,
  phFirstIssueViewed,
  phIssueStatusChanged,
  phFirstIssueResolved,
  phIssueAssigned,
  phEvidenceUploaded,
  phIssueDismissed,
  phIssueDismissalReasonSelected,
  phTourScanInitiated,
} from '../utils/posthogEvents';
import { useAuth } from '../auth.js';
import SolutionModal from './compliance-ui/SolutionModal.jsx';
import ScanFilterModal from './compliance-ui/ScanFilterModal.jsx';
import StandardDropdown from './compliance-ui/StandardDropdown.jsx';

const lastScanStorageKey = (projectId) => `compliance-last-scan-${projectId}`;

/**
 * Fallback PDF-key lookup against the project's document registry (`projectDocs`).
 * The backend only embeds `originalPdfKey` in finding_detail when it can infer it
 * from a flattened document id at scan-write time; when that inference fails the
 * issue carries a doc id with no key. `projectDocs` (from chatbotDocumentDisplay)
 * lists every source document under the project — deliverables AND requirement
 * docs together, with no type field to tell them apart — so this only matches by
 * exact id (a deterministic 1:1 flattening of the literal S3 key). That can only
 * resolve to the same object the issue actually referenced; it deliberately does
 * NOT fall back to title matching, since two different-typed docs could share one.
 */
function resolvePdfKeyFromRegistry(meta, projectDocs) {
  if (!meta || meta.originalPdfKey || !projectDocs?.length) return meta;
  const candidateIds = [meta.id, meta.docId].filter(Boolean).map(String);
  if (!candidateIds.length) return meta;
  const match = projectDocs.find((d) => {
    const ids = [d.id, d.docId, d.s3Key].filter(Boolean).map(String);
    return ids.some((id) => candidateIds.includes(id));
  });
  if (!match) return meta;
  return { ...meta, originalPdfKey: match.originalPdfKey || match.s3Key || null };
}

function latestIssueCreatedAt(issueList) {
  let latest = null;
  let latestMs = 0;
  (issueList || []).forEach((issue) => {
    const raw = issue.createdAt || issue.created_at;
    if (!raw) return;
    const ms = new Date(raw).getTime();
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = raw;
    }
  });
  return latest;
}

function resolveLastScanAt(projectId, issueList) {
  const stored = (() => {
    try {
      return localStorage.getItem(lastScanStorageKey(projectId));
    } catch {
      return null;
    }
  })();
  const fromIssues = latestIssueCreatedAt(issueList);
  if (!stored) return fromIssues;
  if (!fromIssues) return stored;
  const storedMs = new Date(stored).getTime();
  const issueMs = new Date(fromIssues).getTime();
  if (Number.isNaN(storedMs)) return fromIssues;
  if (Number.isNaN(issueMs)) return stored;
  return issueMs > storedMs ? fromIssues : stored;
}

/**
 * Compliance Dashboard – uses components from compliance-ui (mirror html-components)
 * and codiii-kit.css. Reference: src/html-components/Support/compliance-tracker-Feb04.html
 */

/* Styled components kept only where kit has no equivalent (positioning, resizers, filter bubble, etc.) */

const Badge = styled.span`
  display: inline-block; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; text-transform: capitalize;
  ${(p) => p.$variant === 'severity-critical' && 'background: rgba(239,68,68,.15); color:#ef4444;'}
  ${(p) => p.$variant === 'severity-high' && 'background: rgba(245,158,11,.15); color:#f59e0b;'}
  ${(p) => p.$variant === 'severity-medium' && 'background: rgba(59,130,246,.15); color:#3b82f6;'}
  ${(p) => p.$variant === 'severity-low' && 'background: rgba(16,185,129,.15); color:#10b981;'}
  ${(p) => p.$variant === 'status-open' && 'background: rgba(239,68,68,.15); color:#ef4444;'}
  ${(p) => p.$variant === 'status-in-progress' && 'background: rgba(245,158,11,.15); color:#f59e0b;'}
  ${(p) => p.$variant === 'status-review' && 'background: rgba(59,130,246,.15); color:#3b82f6;'}
  ${(p) => p.$variant === 'status-resolved' && 'background: rgba(16,185,129,.15); color:#10b981;'}
`;

const StatusSelect = styled.select`
  padding: 8px 28px 8px 10px; border-radius: 9999px; border: none; color: #111827; font-weight: 700; text-transform: capitalize; cursor: pointer;
  -webkit-appearance: none; appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23333' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
  ${(p) => p.$status === 'resolved' && 'background: rgba(16,185,129,.15); color:#065f46;'}
  ${(p) => p.$status === 'in-progress' && 'background: rgba(59,130,246,.15); color:#1e3a8a;'}
  ${(p) => p.$status === 'review' && 'background: rgba(245,158,11,.15); color:#7c2d12;'}
  ${(p) => p.$status === 'open' && 'background: rgba(239,68,68,.15); color:#7f1d1d;'}
`;

const PillClose = styled.button`
  position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
  width: 16px; height: 16px; border: none; background: transparent; color: #9ca3af; cursor: pointer; padding: 0; line-height: 1;
  opacity: 0; transition: opacity .15s ease; display: inline-flex; align-items: center; justify-content: center;
  &:hover { color: #ef4444; }
`;

const Tag = styled.span`
  display: inline-flex; align-items: center; padding: 6px 22px 6px 10px; border-radius: 9999px; font-size: 12px; font-weight: 600; background: rgba(233,112,36,.12); color: #e97024; margin-right: 6px; margin-bottom: 6px; white-space: nowrap; position: relative; cursor: default; box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  &:hover { background: rgba(233,112,36,.18); box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
  &:hover ${PillClose} { opacity: 1; }
`;

const Code = styled.span`
  font-family: 'Courier New', monospace; font-weight: 600; color: #e97024; background: rgba(233,112,36,0.1); padding: 4px 8px; border-radius: 6px; white-space: nowrap;
`;

const Chip = styled.span`
  background: rgba(233,112,36,0.1); color: #e97024; padding: 4px 22px 4px 10px; border-radius: 16px; font-size: 12px; font-weight: 600; margin: 2px; display: inline-flex; align-items: center; position: relative; box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  &:hover { background: rgba(233,112,36,0.16); box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
  &:hover ${PillClose} { opacity: 1; }
`;

// Image link with hover preview
const ImageLinkWrap = styled.span`
  position: relative;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
`;

const ImageHover = styled.img`
  position: absolute; top: 22px; left: 0; display: none; width: 240px; height: auto;
  border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; box-shadow: 0 10px 24px rgba(0,0,0,0.15); z-index: 100000;
`;

const ImageLink = styled.a`
  color: #1f2937;
  text-decoration: underline;
  font-weight: 600;
  font-size: 12px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover { color: #e97024; }

  ${ImageLinkWrap}:hover & + ${ImageHover} { display: block; }
`;

const PillInput = styled.input`
  border: 1px dashed #e1e5e9; border-radius: 9999px; padding: 4px 10px; height: 28px; outline: none; width: 0; opacity: 0; transition: all .15s ease; margin: 2px; font-size: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.06); min-width: 0; max-width: 100px;
`;

const EditablePills = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  &:hover ${PillInput} { width: auto; min-width: 60px; opacity: 1; }
`;

/* removed duplicate Tag declaration */

const Avatar = styled.div`
  /* Enforce perfectly round, consistent size */
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  aspect-ratio: 1 / 1;
  border-radius: 9999px;
  background: #e97024;
  color: #fff;
  display: inline-grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  text-transform: uppercase;
`;

const RowActions = styled.div`
  display: flex; gap: 8px;
  button { background: rgba(233,112,36,0.1); border: 1px solid rgba(233,112,36,0.2); border-radius: 6px; padding: 6px 12px; font-size: 11px; color: #e97024; font-weight: 600; text-transform: uppercase; cursor: pointer; }
`;

// Column settings modal
const ModalBackdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;
`;

const ModalCard = styled.div`
  background: #fff; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.15); border: 1px solid rgba(0,0,0,0.1);
  width: 90%; max-width: 360px; max-height: 80vh; overflow: auto; padding: 16px 16px 20px 16px;
`;

const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 8px 4px 12px 4px; border-bottom: 1px solid rgba(0,0,0,0.08);
  h3 { margin: 0; font-size: 18px; }
`;

const ModalActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end; padding-top: 12px; border-top: 1px solid rgba(0,0,0,0.08);
`;

const CheckboxRow = styled.label`
  display: flex; align-items: center; gap: 10px; padding: 10px 6px; border-radius: 8px; cursor: pointer;
  &:hover { background: rgba(233,112,36,0.05); }
`;

// Simple filter dropdown
const FilterBubble = styled.div`
  position: absolute; /* anchored to table container */
  background: white; border: 1px solid #e1e5e9; border-radius: 10px; box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  padding: 10px 12px; min-width: 220px; max-width: 300px; z-index: 10000;
  max-height: 280px; overflow: hidden; /* prevent double scrollbar; inner list scrolls */
`;

const HeaderCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  min-width: 0;
  width: 100%;

  .th-content {
    display: block;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .caret { opacity: 0; transition: opacity .15s; cursor: pointer; }
  .filter-dot { display: none; width: 6px; height: 6px; border-radius: 9999px; background: #e97024; }
  &:hover .caret { opacity: 1; }
  &.active .caret { opacity: 1; }
  &.active .filter-dot { display: inline-block; }
`;

const Caret = styled.span`
  width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 6px solid #e97024; display: inline-block;
`;

const Resizer = styled.div`
  position: absolute; top: 0; right: 0; width: 6px; cursor: col-resize; user-select: none; height: 100%;
  &:hover { background: rgba(233,112,36,0.2); }
`;

// formatDate currently unused (date input shows ISO), keeping utility for future export features

const initialData = [
  { id: 1, ncCode: 'NC-001', identifiedIssue: 'Fire rating requirements not met for exterior wall assembly', categories: ['Building Code','Safety'], severity: 'critical', description: 'Exterior wall assembly does not meet required 2-hour fire rating as specified in IBC Section 705.8. Current assembly only provides 1-hour rating.', expectedCompliance: '2-hour fire rating for exterior walls', assignedTo: 'John Smith', deadline: '2024-02-15', status: 'open', tags: ['Fire Safety','Exterior Walls','IBC 705.8'], references: ['Ontario Building Code, 9.10.8.1, Pg 412', 'IBC 705.8, Pg 705-12'], images: ['/logo192.png'] },
  { id: 2, ncCode: 'NC-002', identifiedIssue: 'Insufficient egress width for stairwell exit', categories: ['Building Code','Accessibility'], severity: 'critical', description: 'Stairwell exit width is 32 inches, which is below the minimum required 44 inches for accessible egress routes.', expectedCompliance: 'Minimum 44 inches clear width for accessible egress', assignedTo: 'Sarah Johnson', deadline: '2024-02-20', status: 'in-progress', tags: ['Egress','Accessibility','Stairs'], references: ['Ontario Building Code, 3.4.3.2, Pg 589', 'NFPA 101 7.3.4, Pg 233'], images: [] },
  { id: 3, ncCode: 'NC-003', identifiedIssue: 'HVAC duct clearance conflicts with structural beam', categories: ['Specifications','BIM'], severity: 'high', description: 'HVAC duct routing conflicts with structural beam locations, requiring coordination between mechanical and structural disciplines.', expectedCompliance: 'Proper coordination between MEP and structural systems', assignedTo: 'Mike Chen', deadline: '2024-03-01', status: 'review', tags: ['HVAC','Coordination','Structural'], references: ['Master Project Spec, 23 31 00, Pg 143', 'Structural Drawings S3.2, Grid B/5'], images: [] },
  { id: 4, ncCode: 'NC-004', identifiedIssue: 'Window installation details not compliant with specifications', categories: ['Specifications','Building Code'], severity: 'medium', description: 'Window installation details do not match specified requirements for weatherproofing and thermal performance.', expectedCompliance: 'Window installation per Arch Spec 08 44 13', assignedTo: 'Lisa Rodriguez', deadline: '2024-03-15', status: 'open', tags: ['Windows','Installation','Weatherproofing'], references: ['Master Project Spec, 08 44 13, Pg 62', 'Manufacturer Install Guide, Sec 5.2, Pg 18'], images: [] },
];

// --- API config
const API_BASE = process.env.REACT_APP_COMPLIANCE_API;
const REQUIREMENTS_API = process.env.REACT_APP_API_BASE_REQUIREMENTS || '';

const requirementRegisterCache = { projectId: null, map: null };
let warnedMissingRequirementsApi = false;

async function fetchRequirementRegisterMap(projectId) {
  if (!REQUIREMENTS_API || !projectId) {
    if (!REQUIREMENTS_API && !warnedMissingRequirementsApi) {
      warnedMissingRequirementsApi = true;
      console.warn(
        '[ComplianceDashboard] REACT_APP_API_BASE_REQUIREMENTS is unset; requirement pills will not merge register article'
      );
    }
    return new Map();
  }
  if (requirementRegisterCache.projectId === projectId && requirementRegisterCache.map) {
    return requirementRegisterCache.map;
  }

  const map = new Map();
  let pageToken = null;
  try {
    do {
      const params = new URLSearchParams({ projectId, complianceType: 'ALL' });
      if (pageToken) params.set('pageToken', pageToken);
      const res = await fetch(`${REQUIREMENTS_API}/extracted-requirements?${params.toString()}`, {
        headers: buildAuthHeaders(null),
      });
      if (!res.ok) break;
      const data = await res.json();
      (data.items || []).forEach((item) => {
        if (item?.requirementId) map.set(item.requirementId, item);
      });
      pageToken = data.nextPageToken || null;
    } while (pageToken);
  } catch (err) {
    console.warn('[ComplianceDashboard] requirement register fetch failed:', err);
  }

  requirementRegisterCache.projectId = projectId;
  requirementRegisterCache.map = map;
  return map;
}

function normalizeAndEnrichIssue(it, idx, projectId, registerMap) {
  const rawFinding = it.finding_detail || it.findingDetail;
  const sectionRef =
    coerceSectionRef(it.section_ref || it.sectionRef) ||
    coerceSectionRef(
      typeof rawFinding === 'object' && rawFinding ? rawFinding.section_ref : null
    );

  const base = enrichIssueFields({
    id: it.issueId || it.issue_id || it.id || `${projectId}:${idx}`,
    issueId: it.issueId || it.issue_id || it.id,
    scan_id: it.scan_id || '',
    display_id: it.display_id || '',
    path_type: it.path_type || '',
    topic: it.topic || '',
    sk: it.SK,
    pk: it.PK,
    ncCode: it.display_id || it.ncCode || '',
    identifiedIssue: it.finding_description || it.identifiedIssue || '',
    description: it.finding_description || it.description || '',
    expectedCompliance: it.requirement_text || it.expectedCompliance || '',
    categories: it.categories || (it.compliance_type ? [it.compliance_type] : []),
    compliance_type: it.compliance_type,
    verdict: it.finding_detail?.verdict || null,
    references:
      Array.isArray(it.references) && it.references.length
        ? it.references
        : sectionRef
          ? [sectionRef]
          : [],
    section_ref: sectionRef || '',
    requirement_id: it.requirement_id || '',
    deliverable_doc_ids: it.deliverable_doc_ids || [],
    project_id: it.project_id || projectId,
    images: it.images || [],
    assignedTo: it.assigned_to ?? it.assignedTo,
    deadline: it.deadline || '',
    status: normalizeIssueStatus(it.status),
    tags: it.tags || (it.finding_detail?.verdict ? [it.finding_detail.verdict] : []),
    solutions: it.solutions || [],
    overrideType: it.override_type || it.overrideType || it.finding_detail?.override_type || '',
    dismissalReason:
      it.dismissal_reason || it.dismissalReason || it.finding_detail?.dismissal_reason || '',
    resolutionAttachments: (
      it.resolution_evidence ||
      it.resolution_attachments ||
      it.resolutionAttachments ||
      []
    ).map((att) => ({ ...att, id: att.id || att.solutionId })),
    createdAt: it.created_at || it.createdAt || '',
    updatedAt: it.updated_at || it.updatedAt || '',
    finding_detail: it.finding_detail || it.findingDetail || null,
    sourceDocuments: it.sourceDocuments || it.source_documents,
  });
  return applyRequirementRegisterContext(base, registerMap);
}

// simple fetch wrapper; adds Bearer token when available so API Gateway auth succeeds
async function api(path, { method='GET', body, headers, signal } = {}) {
  const baseHeaders = body ? { 'Content-Type': 'application/json' } : {};
  const idToken = typeof localStorage !== 'undefined' ? localStorage.getItem('id_token') : null;
  const authHeaders = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { ...baseHeaders, ...authHeaders, ...(headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await res.text();
  let data; try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!res.ok) throw Object.assign(new Error(data?.message || res.statusText), { status: res.status, data });
  return data;
}

async function fetchComplianceCheck(projectId, scanId, options = {}) {
  return api(`/projects/${projectId}/scans/${scanId}`, options);
}

async function createComplianceIssue(projectId, checkId, issue) {
  return api(`/projects/${projectId}/compliance/checks/${checkId}/issues`, {
    method: 'POST',
    body: issue
  });
}

async function fetchIssuesByProject(projectId, lastKey = null, options = {}) {
  try {
    const url = `/projects/${projectId}/issues?limit=50${lastKey ? `&lastKey=${encodeURIComponent(lastKey)}` : ''}`;
    const data = await api(url, options);
    return data;
  } catch (err) {
    console.error('Failed to fetch issues:', err);
    throw err;
  }
}

async function fetchIssuesByScan(projectId, scanId, pathType) {
  const q = pathType ? `?path_type=${encodeURIComponent(pathType)}` : '';
  return api(`/projects/${projectId}/scans/${scanId}/issues${q}`);
}

async function fetchScansByProject(projectId) {
  const data = await api(`/projects/${projectId}/scans`);
  return data.scans || [];
}

async function fetchIssuesByCheck(projectId, checkId) {
  if (!checkId || !projectId) return [];

  try {
    const data = await api(`/projects/${projectId}/compliance/checks/${checkId}/issues`);
    return data;
  } catch (err) {
    console.error('Failed to fetch issues by check:', err);
    throw err;
  }
}
async function deleteIssueById(projectId, issueId) {
  return api(`/projects/${projectId}/issues/${issueId}`, {
    method: 'DELETE',
  });
}
async function getSolutionUploadUrl(projectId, issueId, file) {
  return api(`/projects/${projectId}/issues/${issueId}/solutions`, {
    method: 'POST',
    body: {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSize: file.size,
    },
  });
}
async function confirmSolutionUpload(projectId, issueId, solution) {
  return api(`/projects/${projectId}/issues/${issueId}/solutions/confirm`, {
    method: 'POST',
    body: solution,
  });
}
async function uploadFileToPresignedUrl(uploadUrl, file) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!res.ok) {
    throw new Error(`S3 upload failed with status ${res.status}`);
  }

  return true;
}
async function deleteSolutionFromBackend(projectId, issueId, solutionId) {
  return api(`/projects/${projectId}/issues/${issueId}/solutions/${solutionId}`, {
    method: 'DELETE',
  });
}

async function updateComplianceIssue(
  issueId,
  {
    projectId,
    assignedTo,
    status,
    deadline,
    overrideType,
    dismissalReason,
  } = {}
) {
  if (!issueId) throw new Error('issueId required');
  if (!projectId) throw new Error('projectId required');
  const body = {};
  if (assignedTo !== undefined) body.assignedTo = assignedTo;
  if (status !== undefined) body.status = status;
  if (deadline !== undefined) body.deadline = deadline;
  if (overrideType !== undefined) body.override_type = overrideType;
  if (dismissalReason !== undefined) body.dismissal_reason = dismissalReason;
  const encodedId = encodeURIComponent(String(issueId));
  return api(`/projects/${projectId}/issues/${encodedId}`, { method: 'PUT', body });
}

function applyApiIssueToLocalPatch(apiItem) {
  if (!apiItem || typeof apiItem !== 'object') return {};
  return {
    status: apiItem.status,
    assignedTo: apiItem.assigned_to ?? apiItem.assignedTo,
    deadline: apiItem.deadline,
    overrideType: apiItem.override_type ?? apiItem.overrideType,
    dismissalReason: apiItem.dismissal_reason ?? apiItem.dismissalReason,
    resolutionAttachments:
      apiItem.resolution_evidence ?? apiItem.resolution_attachments ?? apiItem.resolutionAttachments,
    updatedAt: apiItem.updated_at ?? apiItem.updatedAt,
  };
}

async function getSolutionDownloadUrl(projectId, issueId, solutionId) {
  return api(`/projects/${projectId}/issues/${issueId}/solutions/${solutionId}`, {
    method: 'GET',
  });
}



export default function ComplianceDashboard({ selectedProject, onOpenDrawingReview }) {
  const auth = useAuth();
  const currentUserId = auth?.user?.sub || null;
  const projectId = selectedProject?.id || null;
  const { t } = useFlexibleTranslation();
  const [query, setQuery] = useState('');
  const [issues, setIssues] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const activeCheckRef = useRef(null);
  const cancelledRef = useRef(false);

  function setActiveScanId(id) {
    setScanId(id);
    activeCheckRef.current = id || null;
    if (id) cancelledRef.current = false;
  }

  const [showColumns, setShowColumns] = useState(false);
  const [newIssue, setNewIssue] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'accordion' | 'table' | 'calendar'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDueFrom, setFilterDueFrom] = useState('');
  const [filterDueTo, setFilterDueTo] = useState('');
  const [filterKeywords, setFilterKeywords] = useState('');
  const [filterTags, setFilterTags] = useState('');
  const [filterPathType, setFilterPathType] = useState('');
  const [filterScanId, setFilterScanId] = useState('');
  const [filterComplianceType, setFilterComplianceType] = useState('');
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: '',
    initialPage: 1,
    title: '',
    highlightText: null,
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const memberNameById = useMemo(() => {
    const map = new Map();
    (projectMembers || []).forEach(m => map.set(m.userId, m.name));
    return map;
  }, [projectMembers]);

  const displayAssigneeName = (userIdOrName) => {
    return memberNameById.get(userIdOrName) || userIdOrName || '';
  };
  const normalizeAssigneeId = useCallback((assignedTo) => {
    if (!assignedTo) return '';
    if (typeof assignedTo === 'string') return assignedTo;

    if (typeof assignedTo === 'object') {
      return assignedTo.userId || assignedTo.id || '';
    }

    return '';
  }, []);
  const categorySuggestions = useMemo(() => ['Building Code','Safety','Accessibility','BIM','Specifications','Structural','HVAC','Windows','Installation','Weatherproofing'], []);

  // add with other useState declarations
  const [scanId, setScanId] = useState(null);
  const [checkStatus, setCheckStatus] = useState(null);
  const [candidateDocs, setCandidateDocs] = useState([]);
  const [pathType, setPathType] = useState(null);
  const [pathReasoning, setPathReasoning] = useState(null);
  const [pathConfidence, setPathConfidence] = useState(null);
  const [lastScanAt, setLastScanAt] = useState(null);
  const [selectedIssueIds, setSelectedIssueIds] = useState(() => new Set());
  const [hideScanAlert, setHideScanAlert] = useState(false);
  const [scanStepIndex, setScanStepIndex] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [completedScanIssueCount, setCompletedScanIssueCount] = useState(null);
  const [activeScanMeta, setActiveScanMeta] = useState({
    complianceType: '',
    complianceSubcategory: '',
    deliverableCount: 1,
  });
  const [recentlyAddedIds, setRecentlyAddedIds] = useState(new Set());


  // Column visibility state
const [visibleCols, setVisibleCols] = useState({
  code: true,
  issue: true,
  categories: true,
  verdict: true,
  created: true,
  description: true,
  expected: true,
  references: true,
  images: true,
  assigned: true,
  deadline: true,
  status: true,
  tags: true,
  actions: true,
});

  // Filter dropdown state
  const [filterAnchor, setFilterAnchor] = useState(null); // { x, y }
  const [filterColumn, setFilterColumn] = useState(null); // key
  const [activeFilters, setActiveFilters] = useState({}); // { key: Set(values) }
  const [filterSearch, setFilterSearch] = useState({}); // { key: query }

  const headerRefs = useRef({});
  const scrollRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const pollAbortRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const issuesFetchInFlightRef = useRef(false);
  const lastRunningIssuesFetchAtRef = useRef(0);
  const showScanModalRef = useRef(false);
  const userDismissedScanModalRef = useRef(false);
  const scanStartedAtRef = useRef(null);
  const RUNNING_ISSUES_FETCH_MS = 5000;
  const resizingRef = useRef({ active: false, key: null, neighbor: null, startX: 0, startWidth: 0, neighborStartWidth: 0 });
  const [columnWidths, setColumnWidths] = useState({});
  const [projectDocs, setProjectDocs] = useState([]);
  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [showScanFilterModal, setShowScanFilterModal] = useState(false);
  const [selectedIssueForSolution, setSelectedIssueForSolution] = useState(null);
  const [selectedSolutionIndex, setSelectedSolutionIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageKey, setNextPageKey] = useState(null);
  const [prevPageKeys, setPrevPageKeys] = useState([]);
  const [totalIssueCount, setTotalIssueCount] = useState(0);

  const mapIssuesFromApi = useCallback(
    async (items, projectId) => {
      if (!projectId) return [];
      const registerMap = await fetchRequirementRegisterMap(projectId);
      return (items || []).map((it, idx) => {
        const issue = normalizeAndEnrichIssue(it, idx, projectId, registerMap);
        return {
          ...issue,
    assignedTo: normalizeAssigneeId(it.assignedTo || it.assigned_to),
        };
      });
    },
    [normalizeAssigneeId]
  );

  useEffect(() => {
    requirementRegisterCache.projectId = null;
    requirementRegisterCache.map = null;
  }, [selectedProject?.id]);

  const filtered = useMemo(() => {
    const listFilters = {
      query,
      filterStatus,
      filterComplianceType,
      filterPathType,
      filterAssigned,
      filterScanId,
      activeStatFilter,
      currentUserId,
    };
    const result = issues.filter((i) => issueMatchesComplianceFilters(i, listFilters));
    const parseNc = (s) => {
      const m = String(s || '').match(/NC-(\d+)/i) || String(s || '').match(/COD-(\d+)/i);
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    return result.slice().sort((a, b) => parseNc(a.ncCode) - parseNc(b.ncCode));
  }, [
    issues,
    query,
    filterStatus,
    filterComplianceType,
    filterPathType,
    filterAssigned,
    filterScanId,
    activeStatFilter,
    currentUserId,
  ]);

  const overviewStats = useMemo(() => computeComplianceOverview(issues), [issues]);
  const issueKpis = useMemo(() => computeIssueKpis(issues), [issues]);

  const pdfHandlers = useMemo(
    () => createPdfReferenceHandlers({ projectId, auth, onOpenViewer: setPdfViewer }),
    [projectId, auth]
  );

  const selectedDeleteCount = useMemo(
    () => issues.filter((i) => selectedIssueIds.has(i.id)).length,
    [issues, selectedIssueIds]
  );

  useEffect(() => {
    setSelectedIssueIds(new Set());
  }, [selectedProject?.id]);

  useEffect(() => {
    showScanModalRef.current = showScanModal;
  }, [showScanModal]);

  useEffect(() => {
    const visible = new Set(filtered.map((i) => i.id));
    setSelectedIssueIds((prev) => {
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const selectedIssue = useMemo(
    () => issues.find((i) => i.id === selectedIssueId) || null,
    [issues, selectedIssueId]
  );

  const savedViewsKey = projectId ? `compliance-saved-views-${projectId}` : null;

  useEffect(() => {
    if (!savedViewsKey) {
      setSavedViews([]);
      return;
    }
    try {
      setSavedViews(JSON.parse(localStorage.getItem(savedViewsKey) || '[]'));
    } catch {
      setSavedViews([]);
    }
  }, [savedViewsKey]);

  const getCurrentViewState = useCallback(
    () => ({
      filterStatus,
      filterComplianceType,
      filterPathType,
      filterAssigned,
      query,
      activeStatFilter,
    }),
    [filterStatus, filterComplianceType, filterPathType, filterAssigned, query, activeStatFilter]
  );

  const handleSaveView = useCallback(
    (name) => {
      if (!savedViewsKey) return;
      const view = { id: String(Date.now()), name, state: getCurrentViewState() };
      const next = [...savedViews, view];
      setSavedViews(next);
      localStorage.setItem(savedViewsKey, JSON.stringify(next));
    },
    [savedViews, savedViewsKey, getCurrentViewState]
  );

  const handleLoadView = useCallback(
    (id) => {
      const view = savedViews.find((v) => v.id === id);
      if (!view?.state) return;
      setFilterStatus(view.state.filterStatus || '');
      setFilterComplianceType(view.state.filterComplianceType || '');
      setFilterPathType(view.state.filterPathType || '');
      setFilterAssigned(view.state.filterAssigned || '');
      setQuery(view.state.query || '');
      setActiveStatFilter(view.state.activeStatFilter || 'all');
    },
    [savedViews]
  );

  const handleDeleteView = useCallback(
    (id) => {
      if (!savedViewsKey) return;
      const next = savedViews.filter((v) => v.id !== id);
      setSavedViews(next);
      localStorage.setItem(savedViewsKey, JSON.stringify(next));
    },
    [savedViews, savedViewsKey]
  );

  const handleOpenRequirementRef = useCallback(
    (ref, issue) => {
      const meta = {
        id:
          issue.requirementRegister?.sourceDocId ||
          issue.requirement_id ||
          issue.requirementId ||
          ref.fileId,
        title:
          issue.requirementRegister?.sourceDocName ||
          issue.requirementArticle ||
          ref.label ||
          ref.value,
        originalPdfKey: ref.originalPdfKey || issue.requirementPdfKey || null,
        pageNumber: ref.pageNumber ?? issue.requirementRegister?.pageNumber ?? null,
      };
      const refForOpen = {
        ...ref,
        originalPdfKey: meta.originalPdfKey,
        value: ref.value || issue.requirementArticle,
        fileId: meta.id,
      };
      if (!refForOpen.originalPdfKey) {
        alert('Cannot open this requirement reference (missing source document).');
        return;
      }
      const type = String(ref.type || '').toLowerCase();
      const page =
        ref.pageNumber ?? meta.pageNumber ?? issue.requirementRegister?.pageNumber;
      if (type === 'page' || (page && refForOpen.originalPdfKey)) {
        pdfHandlers.openPdfRefPage(
          refForOpen,
          meta,
          page || 1
        );
      } else if (refForOpen.value) {
        pdfHandlers.openPdfReference(refForOpen, meta);
      } else {
        pdfHandlers.openPdfPageFromDoc(meta);
      }
    },
    [pdfHandlers]
  );

  const handleOpenEvidenceDoc = useCallback(
    (issue) => {
      const rawMeta = issue.evidenceDocMeta || {};
      const meta = resolvePdfKeyFromRegistry(
        { ...rawMeta, docName: rawMeta.docName || rawMeta.title || issue.evidenceDoc },
        projectDocs
      );
      pdfHandlers.openEvidenceSource(
        {
          docName: issue.evidenceDoc,
          location: issue.evidenceLocation,
          statement: issue.evidenceStatement,
          references: meta.references,
          pageNumber: meta.pageNumber,
          id: meta.id,
          originalPdfKey: meta.originalPdfKey,
          projectId: issue.project_id || projectId,
        },
        meta
      );
    },
    [pdfHandlers, projectId, projectDocs]
  );

  const handleViewOnDrawing = useCallback(
    (issue) => {
      const nav = buildReviewNavStateFromIssue(issue);
      if (nav && onOpenDrawingReview) {
        onOpenDrawingReview(nav);
        return;
      }
      handleOpenEvidenceDoc(issue);
    },
    [onOpenDrawingReview, handleOpenEvidenceDoc]
  );

  const handleOpenSource = useCallback(
    (source) => {
      const resolved = resolvePdfKeyFromRegistry(source, projectDocs);
      pdfHandlers.openEvidenceSource(resolved, null);
    },
    [pdfHandlers, projectDocs]
  );

  async function updateIssue(id, patch) {
    let before = null;
    const statePatch = { ...patch };
    if (statePatch.status !== undefined) {
      statePatch.status = normalizeIssueStatus(statePatch.status);
    }
    if (statePatch.resolutionAttachments) {
      statePatch.resolutionAttachments = statePatch.resolutionAttachments.map(
        ({ id: attId, name, size }) => ({ id: attId, name, size })
      );
    }

    setIssues((prev) => {
      const cur = prev.find((it) => it.id === id);
      before = cur ? { ...cur } : null;
      return prev.map((it) => (it.id === id ? { ...it, ...statePatch } : it));
    });

    const persistKeys = [
      'assignedTo',
      'status',
      'deadline',
      'overrideType',
      'dismissalReason',
      'resolutionAttachments',
    ];
    const shouldPersist = persistKeys.some((k) =>
      Object.prototype.hasOwnProperty.call(patch, k)
    );

    if (!shouldPersist) return;

    const pid = selectedProject?.id;
    const merged = { ...before, ...statePatch };
    const iid = getIssueApiId(merged) || getIssueApiId(before);

    if (!pid || !iid) {
      console.error('[updateIssue] Missing ids', { pid, iid });
      alert('Cannot update issue: missing projectId/issueId');
      if (before) {
        setIssues((prev) => prev.map((it) => (it.id === id ? before : it)));
      }
      return;
    }

    try {
      const result = await updateComplianceIssue(iid, {
        projectId: pid,
        assignedTo: Object.prototype.hasOwnProperty.call(patch, 'assignedTo')
          ? patch.assignedTo
          : undefined,
        status: Object.prototype.hasOwnProperty.call(patch, 'status')
          ? merged.status
          : undefined,
        deadline: Object.prototype.hasOwnProperty.call(patch, 'deadline')
          ? patch.deadline
          : undefined,
        overrideType: Object.prototype.hasOwnProperty.call(patch, 'overrideType')
          ? merged.overrideType
          : Object.prototype.hasOwnProperty.call(patch, 'status')
            ? merged.overrideType
            : undefined,
        dismissalReason: Object.prototype.hasOwnProperty.call(patch, 'dismissalReason')
          ? merged.dismissalReason
          : Object.prototype.hasOwnProperty.call(patch, 'status')
            ? merged.dismissalReason
            : undefined,
      });
      const apiPatch = applyApiIssueToLocalPatch(result?.item);
      if (Object.keys(apiPatch).length) {
        setIssues((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  ...apiPatch,
                  status: normalizeIssueStatus(apiPatch.status ?? it.status),
                }
              : it
          )
        );
      }
    } catch (err) {
      if (before) {
        setIssues((prev) => prev.map((it) => (it.id === id ? before : it)));
      }
      const msg = err?.data?.error || err?.message || 'unknown error';
      alert(`Failed to update issue: ${msg}`);
    }
  }
  function getIssueApiId(issue) {
    return issue?.issueId || issue?.issue_id || issue?.id;
  }

  async function reloadProjectIssues() {
    if (!selectedProject?.id) return;
    try {
      const pageData = await fetchIssuesByProject(selectedProject.id);
      const normalized = await mapIssuesFromApi(pageData.items, selectedProject.id);
      setIssues(normalized);
      setLastScanAt(resolveLastScanAt(selectedProject.id, normalized));
      setNextPageKey(pageData.nextKey || null);
      setPrevPageKeys([]);
      setCurrentPage(1);
      setTotalIssueCount(pageData.totalCount || 0);
    } catch (err) {
      console.error('reload issues failed', err);
    }
  }

  async function deleteIssuesByIds(issueList) {
    const pid = selectedProject?.id;
    if (!pid || !issueList?.length) return { ok: 0, failed: 0 };

    const results = await Promise.allSettled(
      issueList.map((issue) => {
        const iid = getIssueApiId(issue);
        if (!iid) return Promise.reject(new Error('missing issue id'));
        return deleteIssueById(pid, iid);
      })
    );

    const failed = results.filter((r) => r.status === 'rejected').length;
    return { ok: results.length - failed, failed };
  }

  async function deleteIssue(issue) {
    const { failed } = await deleteIssuesByIds([issue]);
    if (failed) {
      alert('Failed to delete issue');
      await reloadProjectIssues();
      return;
    }
    const deleteId = issue.id;
    setIssues((prev) => prev.filter((it) => it.id !== deleteId));
    setSelectedIssueIds((prev) => {
      if (!prev.has(deleteId)) return prev;
      const next = new Set(prev);
      next.delete(deleteId);
      return next;
    });
    if (selectedIssueId === deleteId) setSelectedIssueId(null);
  }

  function toggleIssueSelection(issueId) {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }

  function toggleSelectAllVisibleIssues() {
    const visibleIds = filtered.map((i) => i.id);
    setSelectedIssueIds((prev) => {
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  async function deleteSelectedIssues() {
    const pid = selectedProject?.id;
    if (!pid) {
      alert('Select a project first.');
      return;
    }

    const toDelete = issues.filter((i) => selectedIssueIds.has(i.id));
    if (!toDelete.length) return;

    const label =
      toDelete.length === 1
        ? `Delete issue ${toDelete[0].ncCode || toDelete[0].display_id || ''}?`
        : `Delete ${toDelete.length} issues?`;
    if (!window.confirm(`${label}\n\nThis cannot be undone.`)) return;

    const deleteIds = new Set(toDelete.map((i) => i.id));
    const previous = issues;
    const previousSelection = selectedIssueIds;

    setIssues((prev) => prev.filter((i) => !deleteIds.has(i.id)));
    setSelectedIssueIds(new Set());
    if (selectedIssueId && deleteIds.has(selectedIssueId)) setSelectedIssueId(null);

    const { failed } = await deleteIssuesByIds(toDelete);
    if (failed) {
      setIssues(previous);
      setSelectedIssueIds(previousSelection);
      alert(
        failed === toDelete.length
          ? 'Failed to delete selected issues.'
          : `Failed to delete ${failed} of ${toDelete.length} issues. The list has been refreshed.`
      );
      await reloadProjectIssues();
      return;
    }

    setTotalIssueCount((c) => Math.max(0, (c || 0) - toDelete.length));
  }
  function handleOpenSolutionModal(issue, solutionIndex = null) {
    setSelectedIssueForSolution(issue);
    setSelectedSolutionIndex(solutionIndex);
    setShowSolutionModal(true);
  }

  function handleCloseSolutionModal() {
    setShowSolutionModal(false);
    setSelectedIssueForSolution(null);
    setSelectedSolutionIndex(null);
  }
  function updateIssueSolutions(issueId, updater) {
    setIssues((prev) =>
      prev.map((it) => {
        if (it.id !== issueId) return it;
        const nextSolutions =
          typeof updater === 'function' ? updater(it.solutions || []) : updater;
        return { ...it, solutions: nextSolutions };
      })
    );

    setSelectedIssueForSolution((prev) => {
      if (!prev || prev.id !== issueId) return prev;
      const nextSolutions =
        typeof updater === 'function' ? updater(prev.solutions || []) : updater;
      return { ...prev, solutions: nextSolutions };
    });
  }

  async function handleSolutionUpload(issueId, files) {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;

    const issue = issues.find((it) => it.id === issueId || it.issueId === issueId);
    console.log('[solution upload] incoming', {
      clickedIssueId: issueId,
      foundIssue: issue,
      issueIdFromIssue: issue?.issueId,
      issueCheckId: issue?.checkId,
      activeCheckRef: activeCheckRef.current,
      projectId: selectedProject?.id,
    });
    if (!issue) {
      alert('Could not find issue for upload.');
      return;
    }

    const projectId = selectedProject?.id;

    if (!projectId || !issue.issueId) {
      console.error('Missing IDs for solution upload', {
        projectId,
        issueId,
        issue,
      });
      alert('Cannot upload solution: missing projectId or issueId.');
      return;
    }

    try {
      const uploadedSolutions = [];

      for (const file of fileList) {
        // 1) ask backend for presigned S3 upload URL
        const uploadInfo = await getSolutionUploadUrl(
          projectId,
          issue.issueId,
          file
        );

        // 2) upload binary directly to S3
        await uploadFileToPresignedUrl(uploadInfo.uploadUrl, file);

        // 3) confirm upload so backend saves metadata in DynamoDB
        const confirmRes = await confirmSolutionUpload(
          projectId,
          issue.issueId,
          {
            solutionId: uploadInfo.solutionId,
            name: file.name,
            type: file.type || 'application/octet-stream',
            size: file.size,
            bucket: uploadInfo.bucket,
            s3Key: uploadInfo.s3Key,
          }
        );

        if (confirmRes?.solution) {
          uploadedSolutions.push(confirmRes.solution);
        }
      }

      if (uploadedSolutions.length) {
        updateIssueSolutions(issue.id, (prevSolutions) => [
          ...(prevSolutions || []),
          ...uploadedSolutions,
        ]);

        setSelectedSolutionIndex((prev) => {
          if (prev != null) return prev;
          return 0;
        });
      }
    } catch (err) {
      console.error('Solution upload failed:', err);
      alert(`Failed to upload solution: ${err?.message || 'unknown error'}`);
    }
  }
  async function handleDeleteSolution(issueId, solutionIndex) {
    const ok = window.confirm('Delete this solution?');
    if (!ok) return;

    const issue = issues.find((it) => it.id === issueId || it.issueId === issueId);
    if (!issue) {
      alert('Could not find issue for delete.');
      return;
    }

    const solution = (issue.solutions || [])[solutionIndex];
    if (!solution?.solutionId) {
      alert('Could not find solution to delete.');
      return;
    }

    const projectId = selectedProject?.id;

    if (!projectId || !issue.issueId) {
      console.error('Missing IDs for solution delete', {
        projectId,
        issueId,
        issue,
        solution,
      });
      alert('Cannot delete solution: missing projectId or issueId.');
      return;
    }

    try {
      await deleteSolutionFromBackend(
        projectId,
        issue.issueId,
        solution.solutionId
      );

      updateIssueSolutions(issue.id, (prevSolutions) =>
        (prevSolutions || []).filter((_, idx) => idx !== solutionIndex)
      );

      setSelectedSolutionIndex((prev) => {
        if (prev == null) return null;
        if (prev === solutionIndex) return null;
        if (prev > solutionIndex) return prev - 1;
        return prev;
      });
    } catch (err) {
      console.error('Failed to delete solution:', err);
      alert(`Failed to delete solution: ${err?.message || 'unknown error'}`);
    }
  }
  async function handleGetSolutionDownloadUrl(issue, solution) {
    const projectId = selectedProject?.id;
    const issueIdValue = issue?.issueId || issue?.id;
    const solutionId = solution?.solutionId;

    if (!projectId || !issueIdValue || !solutionId) {
      throw new Error('Missing projectId, issueId, or solutionId for download URL');
    }

    return getSolutionDownloadUrl(
      projectId,
      issueIdValue,
      solutionId
    );
  }
  async function handleResolutionAttachmentsChange(issue, items) {
    const pid = selectedProject?.id;
    const iid = issue?.issueId || issue?.id;
    if (!pid || !iid) {
      alert('Cannot update resolution evidence: missing projectId or issueId.');
      return;
    }

    const existing = issue.resolutionAttachments || [];
    const nextIds = new Set((items || []).map((it) => it.id));
    const removed = existing.filter(
      (att) => att.solutionId && !nextIds.has(att.id)
    );
    const added = (items || []).filter((it) => it.file && !it.solutionId);

    setIssues((prev) =>
      prev.map((it) => (it.id === issue.id ? { ...it, resolutionAttachments: items } : it))
    );

    try {
      for (const att of removed) {
        await deleteSolutionFromBackend(pid, iid, att.solutionId);
      }

      const confirmedRows = [];
      for (const att of added) {
        const file = att.file;
        const uploadInfo = await getSolutionUploadUrl(pid, iid, file);
        await uploadFileToPresignedUrl(uploadInfo.uploadUrl, file);
        const confirmRes = await confirmSolutionUpload(pid, iid, {
          solutionId: uploadInfo.solutionId,
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
          bucket: uploadInfo.bucket,
          s3Key: uploadInfo.s3Key,
        });
        if (confirmRes?.solution) {
          confirmedRows.push({
            ...confirmRes.solution,
            id: confirmRes.solution.id || confirmRes.solution.solutionId,
          });
        }
      }

      setIssues((prev) =>
        prev.map((it) => {
          if (it.id !== issue.id) return it;
          const survivingPersisted = existing.filter(
            (att) => att.solutionId && nextIds.has(att.id)
          );
          return {
            ...it,
            resolutionAttachments: [...survivingPersisted, ...confirmedRows],
          };
        })
      );
    } catch (err) {
      console.error('Resolution evidence sync failed:', err);
      alert(`Failed to save resolution evidence: ${err?.message || 'unknown error'}`);
      await reloadProjectIssues();
    }
  }
  
    // Handle stat card click for filtering
    function handleStatClick(filterKey) {
        if (activeStatFilter === filterKey && filterKey !== 'all') {
        setActiveStatFilter('all');
        } else {
        setActiveStatFilter(filterKey);
        }
    }

  function handleKpiClick(filterKey) {
    handleStatClick(filterKey);
  }

  function handleSelectIssue(issue) {
    if (selectedIssueId !== issue.id) {
      phFirstIssueViewed({ projectId: selectedProject?.id });
    }
    setSelectedIssueId((prev) => (prev === issue.id ? null : issue.id));
    }
    function handleCalendarIssueClick(issue) {
      if (!issue?.id) return;

      setViewMode('accordion');
      setExpandedCardId(issue.id);

      // wait for accordion view to render, then scroll to the opened card
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.getElementById(`compliance-issue-${issue.id}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      });
    }

  const columnOrder = ['code','issue','categories','verdict','created','description','expected','references','images','assigned','deadline','status','tags'];
  const getNextVisibleKey = (key) => {
    const idx = columnOrder.indexOf(key);
    for (let i = idx + 1; i < columnOrder.length; i += 1) {
      const k = columnOrder[i];
      if (visibleCols[k]) return k;
    }
    return null;
  };

  // column resize – ref holds latest logic; stable listeners so add/remove same reference
  const onResizeMoveRef = useRef(() => {});
  onResizeMoveRef.current = (e) => {
    const r = resizingRef.current; if (!r || !r.active) return;
    e.preventDefault();
    const delta = e.clientX - r.startX;
    const minWidth = 80;
    const mainWidth = Math.max(minWidth, r.startWidth + delta);
    setColumnWidths((prev) => ({ ...prev, [r.key]: mainWidth }));
  };
  const handleResizeMove = useCallback((e) => { onResizeMoveRef.current(e); }, []);
  const handleResizeEnd = useCallback(() => {
    resizingRef.current = { active: false, key: null, startX: 0, startWidth: 0 };
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const startResize = (key, event) => {
    event.preventDefault();
    event.stopPropagation();
    const th = headerRefs.current[key];
    if (!th) return;
    const rect = th.getBoundingClientRect();
    resizingRef.current = { active: true, key, startX: event.clientX, startWidth: rect.width };
    document.addEventListener('mousemove', handleResizeMove, { capture: true });
    document.addEventListener('mouseup', handleResizeEnd, { capture: true });
  };

  const applyScanFilter = useCallback(async (scanIdToFilter) => {
    setFilterScanId(scanIdToFilter);
    if (!scanIdToFilter) {
      await reloadProjectIssues();
      return;
    }
    try {
      const data = await fetchIssuesByScan(projectId, scanIdToFilter);
      setIssues(await mapIssuesFromApi(data.items, projectId));
    } catch (e) {
      console.error('scan-scoped fetch failed', e);
    }
  }, [projectId, mapIssuesFromApi]);

  async function handleRunCheck() {
  if (!selectedProject?.id) {
    alert('Select a project first.');
    return;
  }
  if (!promptText?.trim()) {
    alert('Enter a prompt for the compliance scan.');
    return;
  }

  try {
    setIsRunning(true);
    setHideScanAlert(false);

    // 1) start a check
    const started = await api(`/projects/${selectedProject.id}/scans`, {
      method: 'POST',
      body: {
        projectId: selectedProject.id,         
        prompt: promptText,
        userId: currentUserId,
        userEmail: auth?.user?.email || null,                      
      },
    });

    userDismissedScanModalRef.current = false;
    setCandidateDocs([]);
    setPathType(null);
    setPathReasoning(null);
    setPathConfidence(null);
    setScanComplete(false);
    setScanStepIndex(0);
    setActiveScanMeta({ complianceType: '', complianceSubcategory: '', deliverableCount: 1 });
    setActiveScanId(started.scanId);
    setCheckStatus(String(started.status || 'running').trim().toUpperCase());
    setShowScanModal(true); // open modal immediately when user clicks Run
    scanStartedAtRef.current = Date.now();

    // 2) begin polling 
  } catch (e) {
    console.error('startComplianceCheck failed', e);
    alert(`Failed to start scan: ${e.message || 'unknown error'}`);
    setIsRunning(false);
  }
}


  function startAddIssue() {
    if (newIssue) return;
    setNewIssue({
      id: 'draft',
      ncCode: '',
      identifiedIssue: '',
      categories: [],
      description: '',
      expectedCompliance: '',
      references: [],
      images: [],
      assignedTo: '',
      deadline: new Date().toISOString().split('T')[0],
      status: 'open',
      tags: [],
    });
  }

  function cancelAddIssue() {
    setNewIssue(null);
  }

  function computeNextNcCode(list) {
    const nums = (list || [])
      .map((i) => String(i.ncCode || ''))
      .map((s) => {
        const m = s.match(/NC-(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
      });
    const max = nums.length ? Math.max(...nums) : 0;
    const next = max + 1;
    return `NC-${String(next).padStart(3, '0')}`;
  }

async function addIssueFromDraft() {
  if (!newIssue) return;

  const projectId = selectedProject?.id;
  if (!projectId) {
    alert("Missing project ID");
    return;
  }

  const ncCode = computeNextNcCode(issues);
  const payload = { ...newIssue, ncCode };

  try {
    const res = await createComplianceIssue(projectId, payload);
    const newItem = {
      ...payload,
      id: res.issueId,
      issueId: res.issueId,
      scan_id: scanId || '',
      createdAt: res.createdAt
    };
    setIssues(prev => [newItem, ...prev]);
    setNewIssue(null);
  } catch (err) {
    console.error("Create issue failed", err);
    alert("Failed to create issue");
  }
}

  function exportCSV() {
    setShowExportModal(true);
  }

  function handleExport(options) {
    runComplianceExport({
      ...options,
      projectName: selectedProject?.name || '',
      getAssigneeName: displayAssigneeName,
    });
  }

  // Voice input for promptText (front-end only)
  function toggleVoice() {
    if (isRecording) {
      recognitionRef.current?.stop?.();
      setIsRecording(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Your browser does not support speech recognition.');
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const txt = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += txt + ' ';
        else interim += txt;
      }
      setPromptText((finalText + interim).trim());
    };
    rec.onerror = () => setIsRecording(false);
    rec.onend = () => setIsRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setIsRecording(true);
  }

  function openFilter(event, key) {
    event.stopPropagation();
    const caretEl = event.currentTarget;
    if (!caretEl) return;
    const caretRect = caretEl.getBoundingClientRect();
    // position relative to scroll container
    const container = caretEl.closest('[data-scroll-area]');
    const containerRect = container ? container.getBoundingClientRect() : { left: 0, top: 0 };
    const left = Math.max(8, caretRect.left - containerRect.left);
    const top = Math.max(8, caretRect.bottom - containerRect.top + 4);
    setFilterAnchor({ x: left, y: top });
    setFilterColumn(key);
  }
  function handleDismissScanModal() {
    userDismissedScanModalRef.current = true;
    setShowScanModal(false);
  }

  function handleOpenScanModal() {
    if (!selectedProject?.id) {
      alert('Select a project first.');
      return;
    }
    if (!isRunning && !activeCheckRef.current) {
      clearScanState();
    }
    userDismissedScanModalRef.current = false;
    setShowScanModal(true);
  }

  async function handleCancelScan() {
    const idToCancel = scanId || activeCheckRef.current;
    if (idToCancel && projectId) {
      try {
        await api(`/projects/${projectId}/scans/${idToCancel}/cancel`, { method: 'POST' });
      } catch (err) {
        console.error('[cancel] Backend cancel failed:', err);
      }
    }
    userDismissedScanModalRef.current = true;
    setShowScanModal(false);
    setIsRunning(false);
    cancelledRef.current = true;
    activeCheckRef.current = null;
    setScanId(null);
    setScanComplete(false);
    setScanStepIndex(0);
    setCheckStatus(null);
    setCandidateDocs([]);
    setPathType(null);
    setPathReasoning(null);
    setPathConfidence(null);
  }

  const clearScanState = useCallback(() => {
    setScanId(null);
    activeCheckRef.current = null;
    cancelledRef.current = false;
    setPromptText('');
    setScanComplete(false);
    setCompletedScanIssueCount(null);
    setScanStepIndex(0);
    setIsRunning(false);
    setCheckStatus(null);
    setCandidateDocs([]);
    setPathType(null);
    setPathReasoning(null);
    setPathConfidence(null);
    setActiveScanMeta({ complianceType: '', complianceSubcategory: '', deliverableCount: 1 });
  }, []);

  function handleDismissScanProgress() {
    clearScanState();
  }

  useEffect(() => {
    let cancelled = false;

    async function loadProjectDocs() {
      if (!selectedProject?.id) {
        setProjectDocs([]);
        return;
      }

      try {
        const API_URL = process.env.REACT_APP_API_URL_CHATBOT;

        // 1) Ask backend for the raw list
        const listRes = await fetch(`${API_URL}/chatbotDocumentDisplay`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: selectedProject.id })
        });
        const listData = await listRes.json();

        const toIndexDocId = (s3Key) => {
          if (!s3Key || !s3Key.includes('/processed/')) return null;
          const parts = s3Key.split('/');

          const processedIdx = parts.indexOf('processed');
          if (processedIdx < 0 || !parts[processedIdx + 1]) return null;

          const fileSlug = parts[processedIdx + 1]
            .replace(/\.[^/.]+$/, '-txt')   
            .replace(/[_\s]+/g, '-')        
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

          const p = parts.indexOf('projects');
          const r = parts.indexOf('requirements');
          const l = parts.indexOf('links');
          if (p >= 0 && r >= 0 && l >= 0 && parts[p + 1] && parts[r + 1] && parts[l + 1]) {
            return `file-projects-${parts[p + 1]}-requirements-${parts[r + 1]}-links-${parts[l + 1]}-processed-${fileSlug}`;
          }

          const c = parts.indexOf('companies');
          const res = parts.indexOf('resources');
          if (c >= 0 && res >= 0 && l >= 0 && parts[c + 1] && parts[res + 1] && parts[l + 1]) {
            return `file-companies-${parts[c + 1]}-resources-${parts[res + 1]}-links-${parts[l + 1]}-processed-${fileSlug}`;
          }

          return null;
        };

        const stripped = (listData?.documents || []).map(doc => ({
          title: doc.title,
          bucket: doc.s3Bucket,
          s3Key: doc.s3Key,
          originalPdfKey: doc.originalPdfKey || doc.originalS3Key || null,
          category: doc.category || 'technical',
          id: toIndexDocId(doc.s3Key) || doc.id || doc.requirementId || doc.s3Key
        }));

        // 2) Optionally refine/sort via chatbotDocumentDisplayResults.
        // Skip when the raw list is empty - the Lambda returns 404 in that case.
        // Fall back silently to the raw list on any non-2xx response so a
        // transient 400/502 never blanks the document list.
        let refinedDocuments = null;
        if (stripped.length > 0) {
          try {
            const refineRes = await fetch(`${API_URL}/chatbotDocumentDisplayResults`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                documents: stripped,
                context: { projectId: selectedProject.id }
              })
            });
            if (refineRes.ok) {
              const refined = await refineRes.json();
              if (refined?.documents?.length) refinedDocuments = refined.documents;
            }
          } catch (_refineErr) {
            // Non-critical - fall back to raw list.
          }
        }

        // 3) Final list we’ll give to the modal
        const docs = (refinedDocuments || stripped).map((d, i) => ({
          ...d,
          // ensure a stable identifier for selection/de-dupe inside the modal
          docId: d.docId || d.id || d.s3Key || `doc-${i}`,
          title: d.title || d.name || d.filename || 'Untitled Document'
        }));

        if (!cancelled) setProjectDocs(docs);
      } catch (err) {
        console.error('Failed to load project docs for compliance:', err);
        if (!cancelled) setProjectDocs([]);
      }
    }

    loadProjectDocs();
    return () => { cancelled = true; };
  }, [selectedProject?.id]);
  useEffect(() => {
    let cancelled = false;

    async function loadMembers() {
      if (!selectedProject?.id) {
        setProjectMembers([]);
        return;
      }

      try {
        const memberships = await ProjectService.getProjectMembers(selectedProject.id);

        const members = (memberships || []).map((m) => {
          const userId = m?.userId || '';
          const name = m?.user?.name || userId; 
          return { userId, name };
        }).filter(m => m.userId);

        if (!cancelled) setProjectMembers(members);
        } catch (e) {
        console.error('Failed to load project members:', e);
        if (!cancelled) setProjectMembers([]);
      }
    }

    loadMembers();
    return () => { cancelled = true; };
  }, [selectedProject?.id]);


  useEffect(() => {
    const onClick = (e) => {
      if (!(e.target instanceof Node)) return;
      // Close prompt library when clicking outside
      const inPromptLibrary = e.target.closest('.prompt-library-dropdown') || e.target.closest('.prompt-library-btn');
      if (!inPromptLibrary) setShowPromptLibrary(false);
      // Close filter bubble when clicking outside
      const inBubble = document.querySelector('[data-filter-bubble]')?.contains(e.target);
      const inHeader = Object.values(headerRefs.current).some((n) => n?.contains?.(e.target));
      if (!inBubble && !inHeader) {
        setFilterAnchor(null);
        setFilterColumn(null);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (!scanId || !projectId) return;

    let stopped = false;
    let attempt = 0;

    const clearPollTimeout = () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };

    const abortPollFetches = () => {
      if (pollAbortRef.current) {
        pollAbortRef.current.abort();
        pollAbortRef.current = null;
      }
    };

    const schedulePoll = (delay) => {
      if (stopped) return;
      clearPollTimeout();
      pollTimeoutRef.current = setTimeout(poll, delay);
    };

    const mergeRunningIssues = (normalizedIssues) => {
      setIssues((prev) => {
        const prevById = new Map(prev.map((i) => [i.id, i]));
        const newIds = [];

        normalizedIssues.forEach((ni) => {
          if (!prevById.has(ni.id)) {
            newIds.push(ni.id);
          }
          prevById.set(ni.id, ni);
        });

        const merged = Array.from(prevById.values());

        if (newIds.length) {
          setRecentlyAddedIds((prevSet) => {
            const updated = new Set(prevSet);
            newIds.forEach((nid) => updated.add(nid));
            return updated;
          });

          setTimeout(() => {
            setRecentlyAddedIds((prevSet) => {
              const updated = new Set(prevSet);
              newIds.forEach((nid) => updated.delete(nid));
              return updated;
            });
          }, 3000);
        }

        return merged;
      });
    };

    async function refreshProjectIssues(pid, { signal } = {}) {
      const completedData = await fetchIssuesByProject(pid, null, { signal });
      if (stopped) return;
      const normalized = await mapIssuesFromApi(completedData.items, pid);
      setIssues(normalized);
      setNextPageKey(completedData.nextKey || null);
      setPrevPageKeys([]);
      setCurrentPage(1);
      setTotalIssueCount(completedData.totalCount || 0);
      return { totalCount: completedData.totalCount || 0, normalized };
    }

    async function fetchRunningIssuesDebounced(statusLower) {
      const runningStates = ['running', 'awaiting_documents', 'checklisting'];
      if (!runningStates.includes(statusLower)) return;
      const now = Date.now();
      if (now - lastRunningIssuesFetchAtRef.current < RUNNING_ISSUES_FETCH_MS) return;
      if (issuesFetchInFlightRef.current) return;

      issuesFetchInFlightRef.current = true;
      lastRunningIssuesFetchAtRef.current = now;
      const signal = pollAbortRef.current?.signal;

      try {
        const runningPageData = await fetchIssuesByProject(projectId, null, { signal });
        if (stopped) return;
        const normalizedIssues = await mapIssuesFromApi(runningPageData.items, projectId);
        if (stopped) return;
        setTotalIssueCount(runningPageData.totalCount || 0);
        mergeRunningIssues(normalizedIssues);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        console.error('fetchIssuesByProject failed during polling', e);
      } finally {
        issuesFetchInFlightRef.current = false;
      }
    }

    async function poll() {
      if (stopped) return;
      if (pollInFlightRef.current) {
        schedulePoll(2000);
        return;
      }

      pollInFlightRef.current = true;
      abortPollFetches();
      const controller = new AbortController();
      pollAbortRef.current = controller;
      const { signal } = controller;

      try {
        const data = await fetchComplianceCheck(projectId, scanId, { signal });
        if (stopped) return;

        const statusRaw = String(data.status || '').trim();
        const statusUpper = statusRaw.toUpperCase();
        const statusLower = statusRaw.toLowerCase();
        setCheckStatus(statusUpper);
        setPathType(data.path_type || null);
        setPathReasoning(data.pathReasoning || null);
        setPathConfidence(data.pathConfidence || null);

        const issuesFound = data.stats?.issuesFound ?? data.stats?.issues_found;
        setScanStepIndex(
          deriveComplianceScanStepIndex(statusRaw, {
            pathType: data.path_type,
            issuesFound: typeof issuesFound === 'number' ? issuesFound : undefined,
          })
        );

        const preview = data.candidateDocsPreview || [];
        if (preview.length > 0) {
          setCandidateDocs(preview);
        }

        await fetchRunningIssuesDebounced(statusLower);

        if (statusUpper === 'AWAITING_DOCUMENTS' && !showScanModalRef.current && !userDismissedScanModalRef.current) {
          setShowScanModal(true);
          setIsRunning(false);
        }

        if (data.status === 'complete' || data.status === 'completed') {
          setIsRunning(false);
          setScanComplete(true);
          setScanStepIndex(4);
          setPromptText('');
          clearPollTimeout();
          activeCheckRef.current = null;
          const completedAt =
            data.completed_at || data.completedAt || data.updated_at || data.updatedAt || new Date().toISOString();
          if (selectedProject?.id) {
            setLastScanAt(completedAt);
            try {
              localStorage.setItem(lastScanStorageKey(selectedProject.id), completedAt);
            } catch {
              /* ignore */
            }
          }

          if (selectedProject?.id) {
            try {
              const scanIssueCount =
                data.issue_count ?? data.issueCount ?? data.stats?.issuesFound ?? 0;
              setCompletedScanIssueCount(scanIssueCount);
              const { totalCount: issueCount, normalized: freshIssues } =
                await refreshProjectIssues(selectedProject.id, { signal });
              phScanCompleted({
                projectId,
                scanPath: pathType,
                complianceType: activeScanMeta?.complianceType,
                issueCount,
                durationSeconds: scanStartedAtRef.current
                  ? Math.round((Date.now() - scanStartedAtRef.current) / 1000)
                  : null,
              });
              (freshIssues || []).forEach((issue) => {
                phFindingGenerated({
                  projectId,
                  scanId: issue.scan_id,
                  issueId: issue.id || issue.issueId,
                  findingVerdict: issue.verdict,
                  topic: issue.topic,
                  requirementId: issue.requirement_id,
                  hasCitation: !!(issue.section_ref || (Array.isArray(issue.references) && issue.references.length)),
                });
              });
              try {
                window.dispatchEvent(
                  new CustomEvent('codiii:scan-completed', {
                    detail: { projectId, issueCount },
                  })
                );
              } catch (_) { /* CustomEvent unsupported — non-critical */ }
            } catch (e) {
              if (e?.name !== 'AbortError') {
                console.error('fetch issues failed after COMPLETED', e);
              }
            }
          }
          stopped = true;
          return;
        }

        if (data.status === 'failed') {
          setIsRunning(false);
          clearPollTimeout();
          stopped = true;
          activeCheckRef.current = null;
          setScanId(null);
          setShowScanModal(false);

          if (!data.cancelled && !cancelledRef.current) {
            console.error('[poll] Scan failed unexpectedly');
          }
          phScanFailed({
            projectId,
            scanPath: pathType,
            reason: data.cancelled ? 'cancelled' : 'unknown',
            errorMessage: data.cancelled
              ? 'Scan was cancelled before completion.'
              : (data.error || data.message || 'Scan failed unexpectedly — backend does not currently record a reason.'),
          });
          return;
        }

        attempt += 1;
        const analyzingStates = ['running'];
        const delay = analyzingStates.includes(statusLower)
          ? 2000
          : Math.min(1000 * Math.pow(1.5, attempt), 8000);
        schedulePoll(delay);
      } catch (e) {
        if (e?.name === 'AbortError' || stopped) return;
        console.error('poll failed', e);
        attempt += 1;
        const delay = Math.min(1000 * Math.pow(1.5, attempt), 8000);
        schedulePoll(delay);
      } finally {
        pollInFlightRef.current = false;
        if (pollAbortRef.current === controller) {
          pollAbortRef.current = null;
        }
      }
    }

    lastRunningIssuesFetchAtRef.current = 0;
    poll();

    return () => {
      stopped = true;
      clearPollTimeout();
      abortPollFetches();
      pollInFlightRef.current = false;
      issuesFetchInFlightRef.current = false;
    };
  }, [scanId, selectedProject?.id, mapIssuesFromApi]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedProject?.id) {
        setIssues([]);
        setLastScanAt(null);
        return;
      }
      // Active scan poll refreshes issues; avoid duplicate concurrent fetches.
      if (scanId || activeCheckRef.current) return;
      try {
        const pageData = await fetchIssuesByProject(selectedProject.id);
        if (cancelled) return;
        const normalized = await mapIssuesFromApi(pageData.items, selectedProject.id);
        if (cancelled) return;
        setIssues(normalized);
        setLastScanAt(resolveLastScanAt(selectedProject.id, normalized));
        setNextPageKey(pageData.nextKey || null);
        setPrevPageKeys([]);
        setCurrentPage(1);
        setTotalIssueCount(pageData.totalCount || 0);
      } catch (e) {
        console.error('fetchIssuesByProject (on project change) failed', e);
        setIssues([]); // keep UI predictable
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedProject?.id, mapIssuesFromApi, scanId]);
  useEffect(() => {
    if (!visibleCols.code) {
      setVisibleCols((prev) => ({ ...prev, code: true }));
    }
  }, [visibleCols.code]);



const handleScanContinue = async (selectedDocuments, confirmedPathType, confirmedConflictMode, inputs, complianceTypes, subcategory) => {
  if (!scanId || !projectId) return;
  try {
    await api(`/projects/${projectId}/scans/${scanId}/confirm`, {
      method: 'PUT',
      body: {
        // Only send selectedDocIds when NOT doing conflict RvR
        ...(!(confirmedPathType === 'conflict' && confirmedConflictMode === 'rvr') && {
          selectedDocIds: (selectedDocuments || [])
            .map(d => (typeof d === 'string' ? d : d.docId))
            .filter(Boolean),
        }),
        path_type: confirmedPathType,
        conflict_mode: confirmedConflictMode || null,
        inputs: inputs || {},
        compliance_types: complianceTypes || [],
        subcategory: subcategory || null,
      },
    });
    userDismissedScanModalRef.current = true;
    setShowScanModal(false);
    setIsRunning(true);
    setScanComplete(false);
    setScanStepIndex(1);

    const typeKey =
      inputs?.compliance_type ||
      (Array.isArray(complianceTypes) ? complianceTypes[0] : complianceTypes) ||
      '';
    const deliverableCount = Math.max(1, (selectedDocuments || []).length);
    setActiveScanMeta({
      complianceType: complianceTypeLabel(typeKey) || typeKey || 'Compliance',
      complianceSubcategory: subcategory || '',
      deliverableCount,
    });

    phScanPathSelected({ projectId, scanPath: confirmedPathType });
    phScanRun({ projectId, scanPath: confirmedPathType, complianceType: typeKey, deliverableCount });
    phTourScanInitiated();
  } catch (e) {
    console.error('confirm docs failed', e);
    alert(`Failed to confirm documents: ${e.message || 'unknown error'}`);
  }
};

  const pageSubtitle = `${t('compliance.lastScan', 'Last Scan')}: ${formatLastScanAt(lastScanAt)}`;

  const dashboardOverlays = (
    <>
      <ComplianceScanModal
        isOpen={showScanModal}
        projectId={selectedProject?.id}
        onClose={handleDismissScanModal}
        onCancelScan={handleCancelScan}
        onStartScan={handleRunCheck}
        isScanActive={!!scanId || isRunning}
        promptValue={promptText}
        onPromptChange={setPromptText}
        onVoiceToggle={toggleVoice}
        isRecording={isRecording}
        isRunning={isRunning}
        showPromptLibrary={showPromptLibrary}
        onTogglePromptLibrary={() => setShowPromptLibrary((s) => !s)}
        savedPrompts={savedPrompts}
        onSavePrompt={() => {
          if (promptText.trim()) {
            setSavedPrompts((p) => [...p, { id: Date.now(), text: promptText.trim() }]);
            setShowPromptLibrary(false);
          }
        }}
        onUsePrompt={(sp) => {
          setPromptText(sp.text);
          setShowPromptLibrary(false);
        }}
        onDeletePrompt={(id) => setSavedPrompts((p) => p.filter((x) => x.id !== id))}
        promptLibraryEmptyText="No saved prompts yet. Save your current prompt to reuse it."
        candidates={candidateDocs}
        onContinue={handleScanContinue}
        projectDocs={projectDocs}
        pathType={pathType}
        pathReasoning={pathReasoning}
        pathConfidence={pathConfidence}
        awaitingDocuments={checkStatus === 'AWAITING_DOCUMENTS'}
      />

      <ScanFilterModal
        isOpen={showScanFilterModal}
        projectId={selectedProject?.id}
        activeScanId={filterScanId}
        onClose={() => setShowScanFilterModal(false)}
        fetchScans={fetchScansByProject}
        onSelectScan={(sid) => {
          setShowScanFilterModal(false);
          applyScanFilter(sid);
        }}
      />
      <SolutionModal
        isOpen={showSolutionModal}
        issue={selectedIssueForSolution}
        selectedSolutionIndex={selectedSolutionIndex}
        onClose={handleCloseSolutionModal}
        onUpload={handleSolutionUpload}
        onDeleteSolution={handleDeleteSolution}
        onGetDownloadUrl={handleGetSolutionDownloadUrl}
      />
      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        issues={issues}
        filteredIssues={filtered}
      />
      {pdfViewer.open && (
        <PdfViewerModal
          pdfUrl={pdfViewer.url}
          initialPage={pdfViewer.initialPage}
          title={pdfViewer.title}
          highlightText={pdfViewer.highlightText}
          onClose={() =>
            setPdfViewer({ open: false, url: '', initialPage: 1, title: '', highlightText: null })
          }
        />
      )}
    </>
  );

  return (
    <ComplianceDashboardShell
      panelOpen={!!selectedIssue}
      overlays={dashboardOverlays}
      panel={
        <ComplianceDetailPanel
          issue={selectedIssue}
          onClose={() => setSelectedIssueId(null)}
          projectMembers={projectMembers}
          onStatusChange={(v) => {
            if (!selectedIssue) return;
            const fromStatus = selectedIssue.status;
            updateIssue(selectedIssue.id, { status: v });
            phIssueStatusChanged({
              projectId: selectedProject?.id,
              issueId: selectedIssue.id,
              fromStatus,
              toStatus: v,
              overrideType: selectedIssue.overrideType ?? null,
            });
            if (v === 'resolved') {
              phFirstIssueResolved({ projectId: selectedProject?.id });
            }
            if (v === 'dismissed') {
              phIssueDismissed({ projectId: selectedProject?.id, issueId: selectedIssue.id });
            }
          }}
          onAssigneeChange={(v) => {
            if (!selectedIssue) return;
            updateIssue(selectedIssue.id, { assignedTo: v || null });
            phIssueAssigned({ projectId: selectedProject?.id, issueId: selectedIssue.id });
          }}
          onDeadlineChange={(v) => selectedIssue && updateIssue(selectedIssue.id, { deadline: v })}
          onOverrideTypeChange={(v) => {
            if (!selectedIssue) return;
            const patch = { overrideType: v };
            if (normalizeIssueStatus(selectedIssue.status) === 'dismissed') {
              patch.status = 'dismissed';
            }
            updateIssue(selectedIssue.id, patch);
            phIssueDismissalReasonSelected({
              projectId: selectedProject?.id,
              issueId: selectedIssue.id,
              overrideType: v,
            });
          }}
          onDismissalReasonChange={(v) =>
            selectedIssue && updateIssue(selectedIssue.id, { dismissalReason: v })
          }
          onResolutionAttachmentsChange={(items) => {
            if (!selectedIssue) return;
            const previousLength = selectedIssue.resolutionAttachments?.length ?? 0;
            handleResolutionAttachmentsChange(selectedIssue, items);
            if (
              (items || []).length > previousLength &&
              selectedIssue.overrideType === 'requirement_satisfied'
            ) {
              phEvidenceUploaded({ projectId: selectedProject?.id, issueId: selectedIssue.id });
            }
          }}
          onOpenRequirementRef={handleOpenRequirementRef}
          onOpenEvidence={() => selectedIssue && handleOpenEvidenceDoc(selectedIssue)}
          onOpenSource={handleOpenSource}
          onViewOnDrawing={
            DRAWING_REVIEW_ENABLED && selectedIssue && issueHasDrawingReviewTarget(selectedIssue)
              ? () => handleViewOnDrawing(selectedIssue)
              : undefined
          }
          onFilterByScan={applyScanFilter}
        />
      }
    >
      <CompliancePageHeader
        title={t('compliance.title', 'Compliance Dashboard')}
        subtitle={pageSubtitle}
        action={
                        <button
                          type="button"
            className="btn-primary compliance-run-scan-btn"
            onClick={handleOpenScanModal}
            data-tour="new-scan-button"
          >
            {t('compliance.actions.runScan', 'Run Compliance Scan')}
                        </button>
        }
      />

      {(scanId || scanComplete) && (
        <div className="compliance-scan-progress-wrap">
          <ComplianceScanProgress
            complianceType={activeScanMeta.complianceType || 'Building Code'}
            complianceSubcategory={activeScanMeta.complianceSubcategory}
            deliverableCount={activeScanMeta.deliverableCount}
            stepIndex={scanStepIndex}
            isComplete={scanComplete}
            issuesFound={completedScanIssueCount ?? issues.length}
            onDismiss={handleDismissScanProgress}
          />
                          </div>
      )}

      <ComplianceHeroRow
        overviewStats={overviewStats}
        issueKpis={issueKpis}
        activeStatusFilter={activeStatFilter}
        onKpiClick={handleKpiClick}
      />

      <ComplianceFilterBar
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        filterComplianceType={filterComplianceType}
        onFilterComplianceTypeChange={setFilterComplianceType}
        filterPathType={filterPathType}
        onFilterPathTypeChange={setFilterPathType}
        filterAssigned={filterAssigned}
        onFilterAssignedChange={setFilterAssigned}
        projectMembers={projectMembers}
        query={query}
        onQueryChange={setQuery}
        savedViews={savedViews}
        onSaveView={handleSaveView}
        onLoadView={handleLoadView}
        onDeleteView={handleDeleteView}
        onExport={exportCSV}
        onOpenScanFilter={() => setShowScanFilterModal(true)}
        exportLabel={t('compliance.actions.export', 'Export')}
      />

      {filterScanId ? (
        <div className="filter-bar" style={{ marginTop: '-8px' }}>
          <span className="filter-label">
            {t('compliance.filters.scanActive', 'Filtering by originating scan')}
          </span>
          <button
            type="button"
            className="btn-primary btn-primary--small"
            onClick={async () => {
              setFilterScanId('');
              await reloadProjectIssues();
            }}
          >
            {t('compliance.filters.clearScan', 'Clear')}
          </button>
        </div>
      ) : null}

      {selectedDeleteCount > 0 ? (
        <div className="compliance-table-toolbar" role="toolbar" aria-label="Issue selection actions">
          <span className="compliance-table-toolbar__count">
            {t('compliance.table.selectedCount', '{{count}} selected', { count: selectedDeleteCount })}
                            </span>
                          <button
                            type="button"
            className="compliance-table-toolbar__clear"
            onClick={() => setSelectedIssueIds(new Set())}
          >
            {t('compliance.table.clearSelection', 'Clear selection')}
                          </button>
                          <button
                            type="button"
            className="compliance-table-toolbar__delete"
            onClick={deleteSelectedIssues}
          >
            {t('compliance.table.deleteSelected', 'Delete selected')}
                          </button>
                        </div>
      ) : null}

      <ComplianceIssuesTable
        issues={filtered}
        selectedIssueId={selectedIssueId}
        selectedIds={selectedIssueIds}
        onToggleSelect={toggleIssueSelection}
        onToggleSelectAll={toggleSelectAllVisibleIssues}
        onSelectIssue={handleSelectIssue}
        projectMembers={projectMembers}
        displayAssigneeName={displayAssigneeName}
        onOpenEvidenceDoc={handleOpenEvidenceDoc}
        emptyMessage={t('compliance.empty', 'No compliance issues found. Run a scan to check for contradictions.')}
      />

      <div className="compliance-pagination">
        <span className="pagination-info">
          {t('compliance.table.showing', 'Showing {{count}} of {{total}} issues', {
            count: filtered.length,
            total: totalIssueCount || issues.length,
          })}
        </span>
        <div className="pagination-controls">
          <button
            type="button"
            className="page-btn"
            disabled={currentPage === 1}
            aria-label="Previous page"
            onClick={async () => {
              const prevKeys = [...prevPageKeys];
              prevKeys.pop();
              const startKey = prevKeys.length > 0 ? prevKeys[prevKeys.length - 1] : null;
              const data = await fetchIssuesByProject(selectedProject.id, startKey);
              setIssues(await mapIssuesFromApi(data.items, selectedProject.id));
              setNextPageKey(data.nextKey || null);
              setPrevPageKeys(prevKeys);
              setCurrentPage((p) => p - 1);
              setTotalIssueCount(data.totalCount || 0);
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M7.5 9L4.5 6l3-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button type="button" className="page-btn active" aria-current="page">
            {currentPage}
          </button>
          <button
            type="button"
            className="page-btn"
            disabled={!nextPageKey}
            aria-label="Next page"
            onClick={async () => {
              setPrevPageKeys((prev) => [...prev, nextPageKey]);
              const data = await fetchIssuesByProject(selectedProject.id, nextPageKey);
              setIssues(await mapIssuesFromApi(data.items, selectedProject.id));
              setNextPageKey(data.nextKey || null);
              setCurrentPage((p) => p + 1);
              setTotalIssueCount(data.totalCount || 0);
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M4.5 3L7.5 6l-3 3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
            </div>
        </div>
    </ComplianceDashboardShell>
  );
}
